import type { GroupPhoto as PhotoKey } from '../domain.ts';

/**
 * 모임 기본 사진 — 화면 F2-3.
 *
 * **글자 없는 그림 열두 장.** 새벽 · 산 · 바다처럼 무엇을 그린 것인지만
 * 알아보이면 되고, 어떤 모임 이름과 나란히 놓여도 겹쳐 읽히지 않아야 합니다.
 *
 * 사진 파일이 아니라 그라디언트 + 인라인 SVG 입니다. 이유가 셋입니다 —
 * 웹뷰가 네트워크 없이도 그려야 하고, 38px 짜리 목록 얼굴부터 미리 보기까지
 * 같은 그림이 또렷해야 하며, 라이트 · 다크 어느 테마에서도 같은 색이어야
 * 합니다 — 사진은 테마를 따라가지 않는 편이 자연스럽습니다.
 *
 * viewBox 를 60×60 으로 고정하고 xMidYMid slice 로 잘라 채웁니다. 칸이
 * 정사각이 아닐 때 그림이 늘어나지 않고 잘립니다.
 *
 * 키는 server-spring 의 Rules.GROUP_PHOTOS 와 같아야 합니다.
 */

/** 배경. 그림의 하늘 · 땅 색이 여기서 옵니다 */
const BACKGROUND: Record<PhotoKey, string> = {
  dawn: 'linear-gradient(180deg, #27315F 0%, #4A5798 46%, #C99A9A 84%, #E6BFA4 100%)',
  mount: 'linear-gradient(180deg, #9FB0C6 0%, #DCE3EC 100%)',
  sea: 'linear-gradient(180deg, #B8C6E3 0%, #8C9DCB 52%, #5B6BA6 100%)',
  field: 'linear-gradient(180deg, #EDE5CC 0%, #D3DCC4 100%)',
  vine: 'linear-gradient(160deg, #ECE6F2 0%, #C9BEDA 100%)',
  candle: 'radial-gradient(circle at 50% 44%, #5C4B3A 0%, #262130 56%, #17151E 100%)',
  night: 'linear-gradient(180deg, #141A38 0%, #2E3970 100%)',
  olive: 'linear-gradient(180deg, #F0EADB 0%, #DAD0B6 100%)',
  road: 'linear-gradient(180deg, #EBD3CB 0%, #C9A6B6 52%, #8A6E86 100%)',
  cloud: 'linear-gradient(180deg, #A9B8D8 0%, #DCE3F0 100%)',
  wheat: 'linear-gradient(180deg, #F3E3BD 0%, #DDBF7E 100%)',
  ray: 'linear-gradient(180deg, #F6E8CC 0%, #E3C48D 100%)',
};

const ART: Record<PhotoKey, React.ReactNode> = {
  dawn: (
    <>
      <circle cx="46" cy="10" r=".8" fill="#fff" opacity=".7" /><circle cx="14" cy="15" r=".6" fill="#fff" opacity=".55" /><circle cx="31" cy="7" r=".5" fill="#fff" opacity=".5" /><circle cx="30" cy="52" r="11" fill="#F6DDBB" opacity=".95" /><rect x="0" y="50" width="60" height="10" fill="#2C3152" opacity=".72" />
    </>
  ),
  mount: (
    <>
      <path d="M-2 60 L20 24 L31 40 L42 28 L62 60Z" fill="#58748C" /><path d="M20 24 L25 32 L21 30 L17 33Z" fill="#fff" opacity=".7" /><path d="M-2 60 L12 44 L28 60Z" fill="#3F566B" opacity=".85" />
    </>
  ),
  sea: (
    <>
      <circle cx="42" cy="15" r="6" fill="#fff" opacity=".55" /><g fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" opacity=".6"><path d="M-2 36 q8 -4 16 0 t16 0 t16 0 t16 0" /><path d="M-6 45 q8 -4 16 0 t16 0 t16 0 t16 0" /><path d="M-2 54 q8 -4 16 0 t16 0 t16 0 t16 0" /></g>
    </>
  ),
  field: (
    <>
      <circle cx="44" cy="19" r="6" fill="#F2C98A" opacity=".85" /><path d="M0 42 Q18 32 36 40 T60 37 V60 H0Z" fill="#8FAE96" /><path d="M0 50 Q20 42 40 50 T60 47 V60 H0Z" fill="#5F7F7A" />
    </>
  ),
  vine: (
    <>
      <path d="M30 10 C30 16 28 18 30 22" fill="none" stroke="#6B5B4B" strokeWidth="1.6" strokeLinecap="round" /><path d="M31 15 C38 9 46 12 45 19 C40 18 35 19 31 15Z" fill="#6F8F6E" /><g fill="#7E6E9E"><circle cx="24" cy="27" r="4.2" /><circle cx="32.5" cy="27" r="4.2" /><circle cx="28.2" cy="34" r="4.2" /><circle cx="36.8" cy="34" r="4.2" opacity=".85" /><circle cx="20" cy="34" r="4.2" opacity=".85" /><circle cx="24" cy="41" r="4.2" /><circle cx="32.5" cy="41" r="4.2" /><circle cx="28.2" cy="48" r="4.2" /></g>
    </>
  ),
  candle: (
    <>
      <circle cx="30" cy="27" r="15" fill="#F2C98A" opacity=".16" /><path d="M30 17 C34 23 33.5 29 30 30.5 C26.5 29 26 23 30 17Z" fill="#F4C26B" /><path d="M30 22.5 C31.8 25.5 31.6 28.2 30 29 C28.4 28.2 28.2 25.5 30 22.5Z" fill="#FFF1D0" /><rect x="26" y="33" width="8" height="21" rx="1.6" fill="#EDE6DA" />
    </>
  ),
  night: (
    <>
      <path d="M41 11 a11 11 0 1 0 9 17 a8.5 8.5 0 1 1 -9 -17Z" fill="#EFE6C6" /><g fill="#fff"><circle cx="12" cy="14" r=".9" opacity=".8" /><circle cx="21" cy="30" r=".6" opacity=".6" /><circle cx="9" cy="42" r=".7" opacity=".7" /><circle cx="28" cy="10" r=".5" opacity=".6" /><circle cx="36" cy="46" r=".8" opacity=".7" /><circle cx="50" cy="40" r=".5" opacity=".5" /></g>
    </>
  ),
  olive: (
    <>
      <path d="M8 50 C20 42 32 30 52 12" fill="none" stroke="#7A6A4C" strokeWidth="1.6" strokeLinecap="round" /><g fill="#7F8F5E"><ellipse cx="18" cy="40" rx="6" ry="2.4" transform="rotate(-50 18 40)" /><ellipse cx="25" cy="42" rx="6" ry="2.4" transform="rotate(20 25 42)" /><ellipse cx="29" cy="29" rx="6" ry="2.4" transform="rotate(-55 29 29)" /><ellipse cx="37" cy="31" rx="6" ry="2.4" transform="rotate(15 37 31)" /><ellipse cx="41" cy="19" rx="6" ry="2.4" transform="rotate(-60 41 19)" /><ellipse cx="48" cy="21" rx="5" ry="2.2" transform="rotate(10 48 21)" /></g><circle cx="33" cy="36" r="2.4" fill="#5E6B3E" />
    </>
  ),
  road: (
    <>
      <rect x="0" y="40" width="60" height="20" fill="#6E5A6E" opacity=".55" /><path d="M28 40 L32 40 L46 60 L14 60Z" fill="#F1E4DA" opacity=".85" /><circle cx="30" cy="30" r="6" fill="#F6E1D2" opacity=".7" />
    </>
  ),
  cloud: (
    <>
      <g fill="#fff"><path d="M10 38 a7 7 0 0 1 7 -7 a9 9 0 0 1 17 -2 a6.5 6.5 0 0 1 6 9Z" opacity=".95" /><path d="M30 50 a5 5 0 0 1 5 -5 a7 7 0 0 1 13 -1 a5 5 0 0 1 5 6Z" opacity=".75" /></g>
    </>
  ),
  wheat: (
    <>
      <g fill="none" stroke="#9A7A3E" strokeWidth="1.3" strokeLinecap="round"><path d="M22 58 C22 44 23 34 26 22" /><path d="M32 58 C32 44 31 32 30 18" /><path d="M42 58 C41 46 39 36 35 25" /></g><g fill="#B8914A"><ellipse cx="26" cy="20" rx="2" ry="4" transform="rotate(12 26 20)" /><ellipse cx="30" cy="16" rx="2" ry="4" /><ellipse cx="35" cy="23" rx="2" ry="4" transform="rotate(-18 35 23)" /><ellipse cx="24.5" cy="27" rx="1.8" ry="3.4" transform="rotate(-30 24.5 27)" /><ellipse cx="32.5" cy="24" rx="1.8" ry="3.4" transform="rotate(30 32.5 24)" /></g>
    </>
  ),
  ray: (
    <>
      <g stroke="#fff" strokeWidth="2.4" opacity=".55" strokeLinecap="round"><path d="M30 -4 L30 60" /><path d="M30 -4 L12 60" /><path d="M30 -4 L48 60" /><path d="M30 -4 L-2 50" /><path d="M30 -4 L62 50" /></g><circle cx="30" cy="0" r="10" fill="#fff" opacity=".7" />
    </>
  ),
};

interface Props {
  /** 기본 사진의 키(F2-3) 또는 사진첩에서 고른 그림의 데이터 URL(F2-2) */
  photo: PhotoKey | string;
  /** 목록의 얼굴 자리인가 — 모서리를 아바타와 같게 맞춥니다 */
  avatar?: boolean;
}

/** 사진첩에서 고른 그림인가 — 서버도 `data:` 로 둘을 가릅니다 */
export function isUploaded(photo: string): boolean {
  return photo.startsWith('data:');
}

export function GroupPhoto({ photo, avatar }: Props) {
  const className = avatar ? 'photo photo--avatar' : 'photo';

  // 사진첩에서 고른 그림. 서버가 정사각으로 받아 두었지만 cover 로 한 번 더
  // 맞춥니다 — 옛 판이 담아 둔 그림이 정사각이 아닐 수 있습니다
  if (isUploaded(photo)) {
    return <img className={className} src={photo} alt="" aria-hidden="true" />;
  }

  const key = photo as PhotoKey;
  return (
    <span className={className} style={{ background: BACKGROUND[key] }} aria-hidden="true">
      <svg viewBox="0 0 60 60" preserveAspectRatio="xMidYMid slice">{ART[key]}</svg>
    </span>
  );
}
